import type { Position } from "@/types/game.types"

/**
 * Calculate distance between two positions
 */
export function distance(p1: Position, p2: Position): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Calculate squared distance (faster for comparisons)
 */
export function distanceSquared(p1: Position, p2: Position): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return dx * dx + dy * dy
}

/**
 * Check if a point is within range of another point
 */
export function isInRange(p1: Position, p2: Position, range: number): boolean {
  return distanceSquared(p1, p2) <= range * range
}

/**
 * Check if two circles collide
 */
export function circlesCollide(p1: Position, r1: number, p2: Position, r2: number): boolean {
  const combinedRadius = r1 + r2
  return distanceSquared(p1, p2) <= combinedRadius * combinedRadius
}

/**
 * Check if a point is inside a rectangle
 */
export function pointInRect(point: Position, rect: { x: number; y: number; width: number; height: number }): boolean {
  return point.x >= rect.x && point.x <= rect.x + rect.width && point.y >= rect.y && point.y <= rect.y + rect.height
}

/**
 * Find the closest point on a line segment to a given point
 */
export function closestPointOnSegment(point: Position, segStart: Position, segEnd: Position): Position {
  const dx = segEnd.x - segStart.x
  const dy = segEnd.y - segStart.y

  if (dx === 0 && dy === 0) {
    return { ...segStart }
  }

  const t = Math.max(0, Math.min(1, ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / (dx * dx + dy * dy)))

  return {
    x: segStart.x + t * dx,
    y: segStart.y + t * dy,
  }
}

/**
 * Calculate distance from a point to a line segment
 */
export function distanceToSegment(point: Position, segStart: Position, segEnd: Position): number {
  const closest = closestPointOnSegment(point, segStart, segEnd)
  return distance(point, closest)
}

/**
 * Check if a point is on a path (within pathWidth of any segment)
 */
export function isPointOnPath(point: Position, path: Position[], pathWidth: number): boolean {
  for (let i = 0; i < path.length - 1; i++) {
    if (distanceToSegment(point, path[i], path[i + 1]) <= pathWidth / 2) {
      return true
    }
  }
  return false
}

/**
 * Check if a tile overlaps with a path
 */
export function isTileOnPath(
  tileX: number,
  tileY: number,
  path: Position[],
  pathWidth: number,
  tileSize: number,
): boolean {
  // Check 5 points within the tile
  const checkPoints = [
    { x: tileX * tileSize + tileSize / 2, y: tileY * tileSize + tileSize / 2 },
    { x: tileX * tileSize + tileSize * 0.25, y: tileY * tileSize + tileSize * 0.25 },
    { x: tileX * tileSize + tileSize * 0.75, y: tileY * tileSize + tileSize * 0.25 },
    { x: tileX * tileSize + tileSize * 0.25, y: tileY * tileSize + tileSize * 0.75 },
    { x: tileX * tileSize + tileSize * 0.75, y: tileY * tileSize + tileSize * 0.75 },
  ]

  return checkPoints.some((point) => isPointOnPath(point, path, pathWidth))
}

// Legacy exports for backward compatibility
export const circleCollisionSquared = circlesCollide
export const circleCollision = circlesCollide
export const pointInCircleSquared = (point: Position, center: Position, radiusSquared: number) =>
  distanceSquared(point, center) <= radiusSquared
export const pointInCircle = (point: Position, center: Position, radius: number) => isInRange(point, center, radius)
export const pointOnPath = isPointOnPath
